import React, { useState, useEffect } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { MultiSelect } from "@/components/MultiSelect/MultiSelect";
import SearchInput from "@/components/SearchInput/SearchInput";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface FilterMetaItem {
  id: number;
  name: string;
}

interface ResourceFiltersProps {
  filters: {
    searchTerm: string;
    tradeIds: string[];
    resourceTypeIds: string[];
    businessRoleIds: string[];
    hasMigrationErrors?: boolean;
    publishedStatus: "all" | "published" | "unpublished";
  };
  onFilterChange: (
    filterKey: string,
    value: string | string[] | boolean,
  ) => void;
  resourceTypes: FilterMetaItem[];
  trades: FilterMetaItem[];
  businessRoles: FilterMetaItem[];
}

const ResourceFilters: React.FC<ResourceFiltersProps> = ({
  filters,
  onFilterChange,
  resourceTypes,
  trades,
  businessRoles,
}) => {
  const [searchInput, setSearchInput] = useState(filters.searchTerm);
  const debouncedSearch = useDebouncedValue(searchInput, 500);

  useEffect(() => {
    if (debouncedSearch !== filters.searchTerm) {
      onFilterChange("searchTerm", debouncedSearch);
    }
  }, [debouncedSearch, filters.searchTerm, onFilterChange]);

  const resourceTypeOptions = resourceTypes.map((rt) => ({
    label: rt.name,
    value: String(rt.id),
  }));
  const tradeOptions = trades.map((t) => ({
    label: t.name,
    value: String(t.id),
  }));
  const roleOptions = businessRoles.map((br) => ({
    label: br.name,
    value: String(br.id),
  }));

  return (
    <div className="mb-4 space-y-4">
      <SearchInput
        onChange={(val) => setSearchInput(val)}
        placeholder="Search by title or ID..."
        value={searchInput}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <select
          className="px-3 py-2 pr-10 flex h-9 w-full rounded-md border border-input bg-white text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
          onChange={(e) => onFilterChange("publishedStatus", e.target.value)}
          value={filters.publishedStatus}
        >
          <option value="published">Published</option>
          <option value="unpublished">Unpublished</option>
          <option value="all">All statuses</option>
        </select>

        <MultiSelect
          onChange={(newVals) => onFilterChange("resourceTypeIds", newVals)}
          options={resourceTypeOptions}
          placeholder="Select resource types..."
          value={filters.resourceTypeIds}
        />

        <MultiSelect
          onChange={(newVals) => onFilterChange("tradeIds", newVals)}
          options={tradeOptions}
          placeholder="Select trades..."
          value={filters.tradeIds}
        />

        <MultiSelect
          onChange={(newVals) => onFilterChange("businessRoleIds", newVals)}
          options={roleOptions}
          placeholder="Select roles..."
          value={filters.businessRoleIds}
        />
      </div>

      <div className="flex items-center space-x-2 rounded-lg border p-3">
        <Switch
          checked={filters.hasMigrationErrors || false}
          id="migration-errors"
          onCheckedChange={(checked) =>
            onFilterChange("hasMigrationErrors", checked)
          }
        />
        <Label className="cursor-pointer" htmlFor="migration-errors">
          Show resources with migration errors
        </Label>
      </div>
    </div>
  );
};

export default ResourceFilters;
